import { useEffect, useMemo, useState } from 'react';
import type { DragEvent } from 'react';
import type { CreateTaskInput, TaskCollectionRecord, TaskRecord, TaskStatus, UpdateTaskInput } from '@shared/types';
import { subscribeToShellDomainInvalidations } from '@renderer/app/events/shell-domain-events';
import { InlineQuickAddPanel, createQuickAddContext } from '@renderer/app/quick-add/quick-add-shared';
import { useShellStore } from '@renderer/app/state/shell-store';
import {
  CollectionColumnSettingsModal,
  resolveVisibleCollectionColumns,
  type CollectionColumnConfig
} from '@renderer/shared/components/collection-column-settings';
import {
  EmptyState,
  FilterChip,
  EditorTabs,
  IconButton,
  PanelCard,
  SectionHeader,
  SelectField,
  TextField,
  ToolbarGroup
} from '@renderer/shared/components/primitives';
import { Icon } from '@renderer/shared/components/icons';
import { useI18n } from '@renderer/shared/i18n';
import { TaskBoard } from './components/task-board';
import { TaskBoardColumn } from './components/task-board-column';
import { TaskCard } from './components/task-card';
import { TaskEditModal } from './components/task-edit-modal';
import { TaskFocusModal } from './components/task-focus-modal';
import { createTask, deleteTasks, listTasks, moveTaskToStatus, reorderTaskWithinStatus, updateTask } from './tasks-repository';
import {
  applyOptimisticTaskPlacement,
  selectTaskBoardColumns,
  selectTaskCalendarGroups,
  selectTaskLabelOptions,
  selectTaskProjectOptions
} from './tasks-selectors';
import { useTasksViewStore } from './tasks-view-store';

const taskDragMimeType = 'application/x-alttvision-task';
const taskStatuses: TaskStatus[] = ['backlog', 'todo', 'in_progress', 'done', 'archived'];

interface DraggedTaskPayload {
  taskId: string;
  status: TaskStatus;
  boardOrder: number;
}

function readDraggedTaskPayload(event: DragEvent<HTMLElement>): DraggedTaskPayload | null {
  const rawValue = event.dataTransfer.getData(taskDragMimeType) || event.dataTransfer.getData('text/plain');

  if (!rawValue) {
    return null;
  }

  try {
    const payload = JSON.parse(rawValue) as DraggedTaskPayload;
    return payload.taskId ? payload : null;
  } catch {
    return null;
  }
}

export function TasksScreen(): JSX.Element {
  const { locale, t } = useI18n();
  const preferences = useShellStore((state) => state.preferences);
  const savePreferences = useShellStore((state) => state.savePreferences);
  const [collection, setCollection] = useState<TaskCollectionRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const {
    scopeFilter,
    labelFilter,
    projectFilter,
    sort,
    query,
    dragTaskId,
    highlightTaskId,
    setScopeFilter,
    setLabelFilter,
    setProjectFilter,
    setSort,
    setQuery,
    setViewMode,
    startDragging,
    clearDragState,
    setHighlightTaskId
  } = useTasksViewStore();

  const [activeModal, setActiveModal] = useState<{ mode: 'create' } | { mode: 'focus' | 'edit'; taskId: string } | null>(null);
  const taskColumnConfigs = useMemo<CollectionColumnConfig[]>(
    () =>
      taskStatuses.map((status) => ({
        id: status,
        label: t(`enums.taskStatus.${status}`),
        defaultVisible: status !== 'archived'
      })),
    [t]
  );
  const visibleTaskStatusIds = useMemo(
    () =>
      new Set(
        resolveVisibleCollectionColumns(taskColumnConfigs, preferences.collectionColumnVisibility.tasks).map(
          (column) => column.id as TaskStatus
        )
      ),
    [preferences.collectionColumnVisibility.tasks, taskColumnConfigs]
  );

  useEffect(() => {
    let active = true;

    void listTasks()
      .then((nextCollection) => {
        if (active) {
          setCollection(nextCollection);
        }
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Tasks unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  useEffect(
    () =>
      subscribeToShellDomainInvalidations((detail) => {
        if (detail.domain === 'tasks') {
          setReloadToken((value) => value + 1);
        }
      }),
    []
  );

  useEffect(() => {
    if (!highlightTaskId || !collection) {
      return;
    }

    const element = document.querySelector(`[data-task-id="${highlightTaskId}"]`) as HTMLElement | null;
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' });

    const timeoutId = window.setTimeout(() => setHighlightTaskId(null), 2500);
    return () => window.clearTimeout(timeoutId);
  }, [collection, highlightTaskId, setHighlightTaskId]);

  const activeTaskRecord = useMemo(() => {
    if (!activeModal || activeModal.mode === 'create' || !collection) {
      return null;
    }

    return collection.tasks.find((task) => task.id === activeModal.taskId) ?? null;
  }, [activeModal, collection]);

  const activeTaskLabels = useMemo(() => {
    if (!activeTaskRecord || !collection) {
      return [];
    }

    const labelById = new Map(collection.taskLabels.map((label) => [label.id, label.name] as const));
    return activeTaskRecord.taskLabelIds.map((id) => labelById.get(id) ?? null).filter((value): value is string => Boolean(value));
  }, [activeTaskRecord, collection]);

  const statusOptions = useMemo(
    () =>
      (['backlog', 'todo', 'in_progress', 'done', 'archived'] as const).map((value) => ({
        value,
        label: t(`enums.taskStatus.${value}`)
      })),
    [t]
  );

  const projectOptions = useMemo(() => {
    if (!collection) {
      return [{ value: '', label: t('common.notSet') }];
    }

    return [
      { value: '', label: t('common.notSet') },
      ...[...collection.projectSummaries].sort((a, b) => a.name.localeCompare(b.name)).map((project) => ({ value: project.id, label: project.name }))
    ];
  }, [collection, t]);

  async function commitTaskPlacement(payload: DraggedTaskPayload, targetStatus: TaskStatus, targetBoardOrder: number): Promise<void> {
    if (!collection) {
      return;
    }

    if (payload.status === targetStatus && payload.boardOrder === targetBoardOrder) {
      clearDragState();
      return;
    }

    const previousCollection = collection;
    const changedAt = new Date().toISOString();

    setMutationError(null);
    setCollection(
      applyOptimisticTaskPlacement(previousCollection, {
        taskId: payload.taskId,
        targetStatus,
        targetBoardOrder,
        changedAt
      })
    );

    try {
      const nextCollection =
        payload.status === targetStatus
          ? await reorderTaskWithinStatus({
              taskId: payload.taskId,
              status: targetStatus,
              targetBoardOrder
            })
          : await moveTaskToStatus({
              taskId: payload.taskId,
              status: targetStatus,
              targetBoardOrder
            });

      setCollection(nextCollection);
    } catch (nextError) {
      setCollection(previousCollection);
      setMutationError(nextError instanceof Error ? nextError.message : 'Task update failed.');
    } finally {
      clearDragState();
    }
  }

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('tasks.title')} description={error} />
      </div>
    );
  }

  if (!collection) {
    return <div className="av-page" />;
  }

  const columns = selectTaskBoardColumns(collection, {
    locale,
    scopeFilter,
    labelFilter,
    projectFilter,
    sort,
    query,
    t
  });
  const visibleColumns = columns.filter((column) => visibleTaskStatusIds.has(column.status));
  const calendarGroups = selectTaskCalendarGroups(collection, {
    locale,
    scopeFilter,
    labelFilter,
    projectFilter,
    sort,
    query,
    t
  });
  const quickAddContext = createQuickAddContext({
    customers: collection.customerSummaries,
    projects: collection.projectSummaries,
    t
  });

  const newTaskDraft: TaskRecord = {
    id: 'new-task',
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString(),
    deletedAt: null,
    archivedAt: null,
    title: '',
    description: null,
    status: 'todo',
    projectId: projectFilter || null,
    customerId: null,
    assigneeId: null,
    dueAt: null,
    startedAt: null,
    completedAt: null,
    boardOrder: collection.tasks.length,
    taskLabelIds: [],
    priority: 'medium'
  };

  async function handleCreateTask(payload: CreateTaskInput): Promise<void> {
    await createTask(payload);
    setCollection(await listTasks());
  }

  async function handleSaveTask(payload: UpdateTaskInput): Promise<void> {
    await updateTask(payload);
    setCollection(await listTasks());
  }

  async function handleDeleteTask(taskId: string): Promise<void> {
    if (!window.confirm(t('tasks.confirmDelete'))) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await deleteTasks({ ids: [taskId] });
      setCollection(nextCollection);
      setActiveModal(null);
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Task delete failed.');
    }
  }

  function handleVisibleColumnIdsChange(columnIds: string[]): void {
    void savePreferences({
      collectionColumnVisibility: {
        ...preferences.collectionColumnVisibility,
        tasks: columnIds
      }
    });
  }

  function handleResetVisibleColumns(): void {
    const nextVisibility = { ...preferences.collectionColumnVisibility };
    delete nextVisibility.tasks;
    void savePreferences({ collectionColumnVisibility: nextVisibility });
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('tasks.title')}
        actions={
          <button className="av-primary-button" type="button" onClick={() => setActiveModal({ mode: 'create' })}>
            {t('project.tasks.actions.add')}
          </button>
        }
      />

      <InlineQuickAddPanel
        domain="tasks"
        context={quickAddContext}
        onCreated={async (result) => {
          setCollection(await listTasks());
          setHighlightTaskId(result.entityId);
        }}
      />

      <div className="av-toolbar">
        <ToolbarGroup>
          <FilterChip active={scopeFilter === 'all'} icon="tasks" label={t('tasks.filters.all')} onClick={() => setScopeFilter('all')} />
        </ToolbarGroup>

        <ToolbarGroup>
          <SelectField
            options={selectTaskLabelOptions(collection, t)}
            value={labelFilter}
            onChange={(event) => setLabelFilter(event.target.value)}
          />
          <SelectField
            options={selectTaskProjectOptions(collection, t)}
            value={projectFilter}
            onChange={(event) => setProjectFilter(event.target.value)}
          />
          <SelectField
            options={[{ value: 'recent', label: t('tasks.sort.recent') }]}
            value={sort}
            onChange={(event) => setSort(event.target.value as 'recent')}
          />
        </ToolbarGroup>

        <ToolbarGroup>
          <div className="av-page-search av-page-search--compact">
            <Icon name="search" />
            <TextField
              className="av-page-search__input"
              unstyled
              value={query}
              onValueChange={setQuery}
              placeholder={t('tasks.search.placeholder')}
            />
          </div>
          <EditorTabs
            tabs={[
              { id: 'kanban', label: t('tasks.view.kanban'), icon: 'kanban' },
              { id: 'calendar', label: t('tasks.view.calendar'), icon: 'calendar' }
            ]}
            value={preferences.tasksViewMode}
            onChange={(value) => void setViewMode(value)}
          />
          <IconButton
            icon="settings"
            label={t('common.columns.settingsButton')}
            onClick={() => setColumnSettingsOpen(true)}
          />
        </ToolbarGroup>
      </div>

      {mutationError ? <div className="av-inline-error">{mutationError}</div> : null}

      {preferences.tasksViewMode === 'kanban' ? (
        <TaskBoard>
          {visibleColumns.map((column) => (
            <TaskBoardColumn
              key={column.status}
              column={column}
              onDropToColumnEnd={(event) => {
                const payload = readDraggedTaskPayload(event as DragEvent<HTMLElement>);

                if (!payload) {
                  return;
                }

                const fullColumnCount = collection.tasks.filter((task) => task.status === column.status).length;
                const nextBoardOrder = payload.status === column.status ? Math.max(0, fullColumnCount - 1) : fullColumnCount;

                void commitTaskPlacement(payload, column.status, nextBoardOrder);
              }}
            >
              {column.items.length === 0 ? (
                <div className="av-task-board__empty">{t('tasks.empty.column')}</div>
              ) : (
                column.items.map((task) => (
                  <TaskCard
                    key={task.id}
                    isDragging={dragTaskId === task.id}
                    highlighted={highlightTaskId === task.id}
                    task={task}
                    onDragEnd={clearDragState}
                    onDragStart={(event) => {
                      const payload: DraggedTaskPayload = {
                        taskId: task.id,
                        status: column.status,
                        boardOrder: task.boardOrder
                      };

                      event.dataTransfer.effectAllowed = 'move';
                      event.dataTransfer.setData(taskDragMimeType, JSON.stringify(payload));
                      event.dataTransfer.setData('text/plain', JSON.stringify(payload));
                      startDragging(task.id, column.status);
                    }}
                    onDropBefore={(event) => {
                      const payload = readDraggedTaskPayload(event);

                      if (!payload || payload.taskId === task.id) {
                        return;
                      }

                      const nextBoardOrder =
                        payload.status === column.status && payload.boardOrder < task.boardOrder
                          ? Math.max(0, task.boardOrder - 1)
                          : task.boardOrder;

                      void commitTaskPlacement(payload, column.status, nextBoardOrder);
                    }}
                    onFocus={(taskId) => setActiveModal({ mode: 'focus', taskId })}
                    onEdit={(taskId) => setActiveModal({ mode: 'edit', taskId })}
                    onDelete={(taskId) => void handleDeleteTask(taskId)}
                    strings={{
                      focus: t('tasks.card.actions.focus'),
                      edit: t('tasks.card.actions.edit'),
                      delete: t('tasks.card.actions.delete')
                    }}
                  />
                ))
              )}
            </TaskBoardColumn>
          ))}
        </TaskBoard>
      ) : (
        <PanelCard title={t('tasks.calendar.title')}>
          {calendarGroups.length === 0 ? (
            <EmptyState title={t('tasks.calendar.emptyTitle')} description={t('tasks.calendar.emptyDescription')} />
          ) : (
            <div className="av-task-calendar">
              {calendarGroups.map((group) => (
                <section key={group.label} className="av-task-calendar__group">
                  <header>
                    <h2>{group.label}</h2>
                    <span>{group.items.length}</span>
                  </header>
                  <div className="av-task-calendar__list">
                    {group.items.map((item) => (
                      <PanelCard key={item.id} className="av-task-calendar__item">
                        <div className="av-task-calendar__item-copy">
                          <strong>{item.title}</strong>
                          {item.description ? <p>{item.description}</p> : null}
                          <div className="av-inline-metadata">
                            <span>{item.dueLabel}</span>
                            {item.context ? <span>{item.context.primaryLabel}</span> : null}
                          </div>
                        </div>
                      </PanelCard>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </PanelCard>
      )}

      {activeModal?.mode === 'focus' && activeTaskRecord ? (
        <TaskFocusModal
          isOpen
          locale={locale}
          title={activeTaskRecord.title}
          task={activeTaskRecord}
          labels={activeTaskLabels}
          statusLabel={t(`enums.taskStatus.${activeTaskRecord.status}`)}
          onClose={() => setActiveModal(null)}
          onEdit={() => setActiveModal({ mode: 'edit', taskId: activeTaskRecord.id })}
          onDelete={() => void handleDeleteTask(activeTaskRecord.id)}
          strings={{
            close: t('common.close'),
            edit: t('tasks.modal.edit'),
            delete: t('shell.delete'),
            dueAt: t('tasks.modal.dueAt'),
            labels: t('tasks.modal.labels'),
            description: t('tasks.modal.description'),
            noDescription: t('tasks.modal.noDescription'),
            noDeadline: t('common.noDeadline')
          }}
        />
      ) : null}

      {activeModal?.mode === 'create' ? (
        <TaskEditModal
          isOpen
          mode="create"
          task={newTaskDraft}
          statusOptions={statusOptions}
          projectOptions={projectOptions}
          onClose={() => setActiveModal(null)}
          onSave={handleCreateTask}
          strings={{
            close: t('common.close'),
            title: t('tasks.modal.createTitle'),
            save: t('common.applyChanges'),
            cancel: t('common.cancel'),
            fieldTitle: t('quickAdd.fields.title'),
            fieldDescription: t('quickAdd.fields.description'),
            fieldStatus: t('tasks.fields.status'),
            fieldProject: t('quickAdd.fields.project'),
            fieldDueAt: t('quickAdd.fields.dueAt'),
            validationTitleRequired: t('quickAdd.validation.titleRequired')
          }}
        />
      ) : null}

      {activeModal?.mode === 'edit' && activeTaskRecord ? (
        <TaskEditModal
          isOpen
          task={activeTaskRecord}
          statusOptions={statusOptions}
          projectOptions={projectOptions}
          onClose={() => setActiveModal(null)}
          onSave={handleSaveTask}
          strings={{
            close: t('common.close'),
            title: t('tasks.modal.editTitle'),
            save: t('common.applyChanges'),
            cancel: t('common.cancel'),
            fieldTitle: t('quickAdd.fields.title'),
            fieldDescription: t('quickAdd.fields.description'),
            fieldStatus: t('tasks.fields.status'),
            fieldProject: t('quickAdd.fields.project'),
            fieldDueAt: t('quickAdd.fields.dueAt'),
            validationTitleRequired: t('quickAdd.validation.titleRequired')
          }}
          />
      ) : null}

      <CollectionColumnSettingsModal
        columns={taskColumnConfigs}
        domain="tasks"
        isOpen={columnSettingsOpen}
        strings={{
          title: t('common.columns.settingsTitle'),
          description: t('common.columns.settingsDescription'),
          reset: t('common.columns.settingsReset'),
          close: t('common.close'),
          locked: t('common.columns.settingsLocked')
        }}
        visibleColumnIds={preferences.collectionColumnVisibility.tasks}
        onClose={() => setColumnSettingsOpen(false)}
        onReset={handleResetVisibleColumns}
        onVisibleColumnIdsChange={handleVisibleColumnIdsChange}
      />
    </div>
  );
}
